import { merge } from "lodash";
import uuid from "uuid-random";
import { spawnParamsToStartOptions, toGoLang, nullish, safeInteropRegister } from "../../api-polyfill/src/common/windowFunctions";
const isTokenError = (tok) => {
    return tok.error !== undefined;
};
const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
};
const generateCSRFToken = () => {
    const epoch = new Date();
    let id = epoch.getTime().toString(36);
    id = id + Math.floor(Math.random() * Math.pow(36, 2)).toString(36);
    return id.toUpperCase();
};
const digestHex = async (message) => {
    const msgUint8 = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const uintArr = new Uint8Array(hashBuffer);
    const uintString = String.fromCharCode(...uintArr);
    const binaryString = window.btoa(uintString);
    return binaryString
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");
};
const randomBase64Generator = () => window.btoa(uuid());
export class OAuth {
    constructor(moduleRoot) {
        this.moduleRoot = moduleRoot;
        this.csfrTokens = {};
    }
    setSearchParams(url, state, config) {
        const { searchParams } = url;
        searchParams.set("client_id", config.client_id);
        searchParams.set("response_type", config.backchannel_endpoint ? "code" : "token");
        searchParams.set("redirect_uri", config.redirect_uri);
        searchParams.set("state", state);
        if (config.nonce) {
            searchParams.set("nonce", generateCSRFToken());
        }
        if (config.scope) {
            searchParams.set("scope", config.scope);
        }
        return searchParams.toString();
    }
    validateConfiguration(config) {
        if (!config.authorization_endpoint) {
            return "Missing required field authorization_endpoint";
        }
        if (!config.client_id) {
            return "Missing required field client_id";
        }
        if (config.pkce && !config.backchannel_endpoint) {
            return "backchannel_endpoint must be configured when using PKCE. Set it to the endpoint for your provider's token url";
        }
        return null;
    }
    async getWindowCreateOptionsFromConfig(glue, appName) {
        if (!appName) {
            return {};
        }
        try {
            const config = await glue.appManager.application(appName);
            return config?.windowSettings ?? {};
        }
        catch (e) {
            return {};
        }
    }
    async authenticate(glue, profileName, preloadScripts, config, spawnParams, cb, handler, logger) {
        const maybeError = this.validateConfiguration(config);
        if (maybeError) {
            logger.error(`authentication - OAuth.authenticate() error ${maybeError}`, config);
            cb(maybeError);
            return;
        }
        const codeVerifier = randomBase64Generator();
        const codeChallenge = await digestHex(codeVerifier);
        const completeConfig = {
            ...config,
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
            grant_type: config.grant_type ?? "authorization_code",
            redirect_uri: config.redirect_uri ??
                `${this.moduleRoot}/ui-components/oauthResponse/index.html`,
        };
        const state = generateCSRFToken();
        this.csfrTokens[state] = {
            config: completeConfig,
            cb,
            handler,
            profileName,
            codeVerifier,
        };
        try {
            const url = new URL(completeConfig.authorization_endpoint);
            url.search = this.setSearchParams(url, state, completeConfig);
            const defaultWindowCreateOptions = {
                autoSnap: false,
                isSticky: false,
                placement: {
                    snapped: false,
                    verticalAlignment: "center",
                    horizontalAlignment: "center",
                    height: 600,
                    width: 480,
                },
            };
            const configParams = await this.getWindowCreateOptionsFromConfig(glue, completeConfig.component);
            const overrideParams = await spawnParamsToStartOptions(glue, spawnParams, {});
            const requiredParams = {
                url: url.toString(),
                mode: "flat",
                channelSelector: {
                    enabled: false
                },
                context: {
                    OAuthState: state,
                    authorizationUrl: url.toString(),
                },
                preloadScripts,
            };
            const windowCreateOptions = merge(defaultWindowCreateOptions, configParams, overrideParams, requiredParams);
            logger.debug("authentication - Oauth.authenticate() windowCreateOptions", windowCreateOptions);
            const { err, data: gdWindow } = await toGoLang(() => glue.windows.open(uuid(), url.toString(), windowCreateOptions));
            if (err || !gdWindow) {
                logger.error("authentication - Oauth.authenticate() Failed to spawn pop up", err);
                cb(err);
                return;
            }
            logger.info("authentication - Authentication pop-up window spawned");
        }
        catch (e) {
            const error = `authentication - OAUTH2 failed: ${e.message}`;
            logger.error(error);
            cb(error);
        }
    }
    async responseFromAuthorizationEndpoint(err, redirectResponse, logger) {
        const maybeError = (state, transaction) => {
            const maybeStateError = state
                ? null
                : "No state returned from endpoint";
            const maybeTransactionError = transaction
                ? null
                : `Cannot find OAUTH state ${state}`;
            return err ?? maybeStateError ?? maybeTransactionError;
        };
        logger.debug("authentication - OAuth.responseFromAuthorizationEndpoint() received from completeOAuth() call =", redirectResponse);
        const { state, code } = redirectResponse;
        const transaction = this.csfrTokens[state];
        logger.debug("authentication - OAuth.responseFromAuthorizationEndpoint() transaction =", transaction, "state=", state);
        const error = maybeError(state, transaction);
        if (error) {
            if (transaction) {
                const { cb } = transaction;
                cb(error);
                delete this.csfrTokens[state];
            }
            return { err: error };
        }
        const { config, cb } = transaction;
        const { err: tokenRetrievalError, data: tokenResponse } = config.backchannel_endpoint
            ? await this.retrieveAccessToken(state, code, logger)
            : { err: null, data: redirectResponse };
        if (tokenRetrievalError) {
            delete this.csfrTokens[state];
        }
        else {
            this.csfrTokens[state].credentials = tokenResponse;
            this.setupTokenExpirationOrRefresh(tokenResponse, logger);
        }
        cb?.(tokenRetrievalError, { data: tokenResponse });
        return {
            err: tokenRetrievalError,
            data: tokenResponse,
        };
    }
    async retrieveAccessToken(state, code, logger) {
        const transaction = this.csfrTokens[state];
        const { config, profileName, codeVerifier } = transaction;
        if (!config.backchannel_endpoint) {
            return { err: "No backchannel_endpoint available" };
        }
        const { client_id, redirect_uri, grant_type, scope } = config;
        const formData = {
            client_id,
            code,
            redirect_uri,
            state,
        };
        if (grant_type) {
            formData.grant_type = grant_type;
        }
        if (scope) {
            formData.scope = scope;
        }
        if (codeVerifier) {
            formData.code_verifier = codeVerifier;
        }
        if (config.client_secret) {
            formData.client_secret = config.client_secret;
        }
        const payload = new URLSearchParams(formData).toString();
        logger.debug("authentication - OAuth.retrieveAccessToken() fetching token", config.backchannel_endpoint, payload, state, profileName);
        const { err, data: token } = await this.fetchAccessToken(config.backchannel_endpoint, payload, profileName, logger);
        if (config.userProfile_endpoint && token?.access_token) {
            const { err: userErr, data: userData } = await this.fetchUserData(config.userProfile_endpoint, token?.access_token, logger);
            return {
                err: userErr,
                data: {
                    ...token,
                    ...userData,
                },
            };
        }
        else {
            return { err, data: token };
        }
    }
    async fetchAccessToken(endpoint, payload, profileName, logger) {
        try {
            const response = await fetch(endpoint, {
                mode: "cors",
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Accept: "application/json",
                },
                body: payload,
            });
            const token = await response.json();
            if (isTokenError(token)) {
                const err = token.error +
                    (token.error_description
                        ? " - " + token.error_description
                        : "");
                logger.warn("authentication - OAuth.fetchAccessToken()", err);
                return { err };
            }
            const moreCredentials = token.id_token
                ? parseJwt(token.id_token)
                : {};
            const credentials = merge({ name: profileName, profileName }, token, moreCredentials);
            logger.debug("authentication - OAuth.fetchAccessToken() successful token fetch", credentials);
            return { err: null, data: credentials };
        }
        catch (e) {
            logger.warn("authentication - OAuth.fetchAccessToken()", e.message);
            return { err: e.message };
        }
    }
    async fetchUserData(endpoint, accessToken, logger) {
        try {
            const getUserInfo = await fetch(endpoint, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await getUserInfo.json();
            logger.debug("authentication - OAuth.fetchUserData() successful user data fetch", data);
            return { err: null, data };
        }
        catch (e) {
            logger.warn("authentication - OAuth.fetchUserId()", e.message);
            return { err: e.message };
        }
    }
    async refreshToken(redirectResponse, logger) {
        const { state, code } = redirectResponse;
        const transaction = this.csfrTokens[state];
        const { config } = transaction;
        const numRetries = config.refreshRetries ?? 3;
        const retryDelaySecs = config.refreshRetryDelaySeconds ?? 60;
        for (let i = 0; i < numRetries; i++) {
            const { err, data } = await this.retrieveAccessToken(state, code, logger);
            if (!err) {
                return { err: null, data };
            }
            logger.info(`authentication - Retrying Access token refresh (retry ${i} of ${numRetries}) in ${retryDelaySecs} seconds`);
            await new Promise((sleep) => setTimeout(sleep, retryDelaySecs * 1000));
        }
        const err = "Access token refresh retries exhausted. The access token was NOT refreshed.";
        return { err };
    }
    setupTokenExpirationOrRefresh(redirectResponse, logger) {
        const { state } = redirectResponse;
        const transaction = this.csfrTokens[state];
        const { config, handler, credentials } = transaction;
        const updateTokenOrError = (err, data) => {
            if (err) {
                delete this.csfrTokens[state];
            }
            handler(err, { data });
        };
        if (!credentials || !credentials.expires_in) {
            const error = `authentication - Access token for ${transaction.profileName} does not contain expires_in. Expiring immediately.`;
            logger.warn(error);
            delete this.csfrTokens[state];
            handler(null, { data: credentials, remove: true });
            return;
        }
        const preemptExpiryBySecs = config.refreshPreemptExpirySeconds ?? 300;
        const shouldRefresh = credentials.refresh_token &&
            (config.refreshAccessToken || nullish(config.refreshAccessToken));
        const timer = credentials.expires_in - shouldRefresh ? preemptExpiryBySecs : 0;
        logger.info(`authentication - Access token expires in ${credentials.expires_in} seconds, setting up automated refresh in ${timer} seconds`);
        const expireOrRefresh = async () => {
            if (shouldRefresh) {
                const { err, data } = await this.refreshToken(redirectResponse, logger);
                this.csfrTokens[state].credentials = {
                    ...this.csfrTokens[state].credentials,
                    ...data,
                };
                this.setupTokenExpirationOrRefresh(redirectResponse, logger);
                updateTokenOrError(err, data);
            }
            else {
                updateTokenOrError(`Token expired for ${transaction.profileName}`);
            }
        };
        setTimeout(expireOrRefresh, timer * 1000);
    }
}
export class AuthenticationService {
    constructor(glue, logger, configService, moduleRoot) {
        this.glue = glue;
        this.logger = logger;
        this.configService = configService;
        this.activeProfiles = {};
        this.oauth = new OAuth(moduleRoot);
    }
    async beginAuthentication({ preloadScripts, profile, spawnParams, handlerName, }) {
        return new Promise((resolve) => {
            const config = this.configService.config.authentication;
            const authenticationProfile = profile && config
                ? {
                    ...config[profile],
                    name: profile,
                }
                : {};
            const activeProfile = this.activeProfiles[profile];
            if (activeProfile?.credentials) {
                this.logger.debug(`authentication - beginAuthentication(): Authentication operation for ${profile} returned credentials without a new transaction`);
                activeProfile.handlerNames.push(handlerName);
                this.glue.interop.invoke(handlerName, {
                    err: null,
                    data: activeProfile.credentials,
                });
                resolve({ err: null, data: activeProfile.credentials });
                return;
            }
            const inFlight = activeProfile ? activeProfile.inFlightCallbacks.length > 0 : false;
            this.activeProfiles[profile] = {
                inFlightCallbacks: (activeProfile?.inFlightCallbacks ?? []).concat(resolve),
                handlerNames: (activeProfile?.handlerNames ?? []).concat(handlerName),
            };
            const allHandlers = (err, result) => {
                const { data, remove } = result ?? {
                    data: null,
                    remove: false,
                };
                const myActiveProfile = this.activeProfiles[profile];
                if (myActiveProfile) {
                    myActiveProfile.handlerNames.forEach((handlerName) => {
                        this.glue.interop.invoke(handlerName, { err, data });
                    });
                    if (err || remove) {
                        this.logger.info(`authentication - Removing active profile ${profile}`, err);
                        delete this.activeProfiles[profile];
                    }
                    else {
                        this.logger.debug(`authentication - Saving credentials to active profile ${profile}`);
                        myActiveProfile.credentials = data;
                    }
                }
            };
            const allCallbacks = (err, result) => {
                const { data } = result ?? { data: null };
                const myActiveProfile = this.activeProfiles[profile];
                if (myActiveProfile) {
                    myActiveProfile.inFlightCallbacks.forEach((cb) => cb({ err, data }));
                    myActiveProfile.inFlightCallbacks = [];
                }
                allHandlers(err, result);
            };
            if (!inFlight) {
                this.logger.debug(`authentication - beginAuthentication(): Authentication operation for ${profile} started at ${Date.now()}`);
                this.oauth.authenticate(this.glue, profile, preloadScripts, authenticationProfile, spawnParams, allCallbacks, allHandlers, this.logger);
            }
            else {
                this.logger.debug(`authentication - beginAuthentication(): In flight operation for ${profile} at ${Date.now()}`);
            }
        });
    }
    async completeOAUTH({ err, data, }) {
        return this.oauth.responseFromAuthorizationEndpoint(err, data, this.logger);
    }
    async publishAuthorization(params) {
        this.logger.debug("authentication - Received publishAuthorization credentials. Relaying to sso-application.");
        await this.glue.interop.invoke("Finsemble.Clients.AuthenticationClient.publishAuthorization.backChannel", params);
    }
    async start(fsblPreload) {
        const preloadScripts = [fsblPreload];
        await this.glue.interop.register("FSBL.Clients.AuthenticationClient.beginAuthentication", safeInteropRegister(async (params) => this.beginAuthentication({
            preloadScripts,
            ...params,
        }), this.logger));
        await this.glue.interop.register("FSBL.Clients.AuthenticationClient.completeOAUTH", safeInteropRegister(async (params) => this.completeOAUTH(params), this.logger));
        await this.glue.interop.register("FSBL.Clients.AuthenticationClient.publishAuthorization", safeInteropRegister(async (params) => this.publishAuthorization(params), this.logger));
    }
}
